import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import AddressInputForm from './components/AddressInputForm';
import MetricsCards from './components/MetricsCards';
import ActiveInvestigationsTable from './components/ActiveInvestigationsTable';
import RecentActivityFeed from './components/RecentActivityFeed';
import InvestigationTemplates from './components/InvestigationTemplates';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

// Replace mock data with Supabase services
import { investigationService, templateService } from '../../services/investigationService';

const InvestigationDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [isLoading, setIsLoading] = useState(true);

  // Real data state
  const [metrics, setMetrics] = useState({
    totalInvestigations: 0,
    activeInvestigations: 0,
    completedInvestigations: 0,
    flaggedAddresses: 0,
    totalFundsTraced: '0.00'
  });
  const [activeInvestigations, setActiveInvestigations] = useState([]);
  const [recentActivities, setRecentActivities] = useState([]);
  const [templates, setTemplates] = useState([]);

  const handleStartInvestigation = async (investigationData) => {
    try {
      // Create new investigation in database
      const result = await investigationService?.createInvestigation({
        case_number: `CASE-${Date.now()}`,
        title: investigationData?.title || 'New Investigation',
        description: investigationData?.description,
        priority: investigationData?.priority || 3,
        target_addresses: investigationData?.addresses ? [investigationData?.addresses] : []
      });

      if (result?.error) {
        console.error('Failed to create investigation:', result?.error);
        return;
      }

      // Navigate to transaction analysis with the investigation data
      navigate('/transaction-analysis', { 
        state: { 
          ...investigationData,
          investigation: result?.data,
          timestamp: new Date()?.toISOString()
        }
      });
    } catch (error) {
      console.error('Investigation creation error:', error);
    }
  };

  const handleSelectTemplate = async (templateData) => {
    try {
      // Increment template usage
      await templateService?.useTemplate(templateData?.id);
      
      // Navigate to transaction analysis with template data
      navigate('/transaction-analysis', { 
        state: { 
          ...templateData,
          template: templateData,
          timestamp: new Date()?.toISOString()
        }
      });
    } catch (error) {
      console.error('Template selection error:', error);
    }
  };

  const loadDashboardData = async () => {
    try {
      setIsLoading(true);

      // Load dashboard metrics
      const metricsResult = await investigationService?.getDashboardMetrics();
      if (!metricsResult?.error && metricsResult?.data) {
        setMetrics(metricsResult?.data);
      }

      // Load active investigations
      const investigationsResult = await investigationService?.getInvestigations({
        status: 'active'
      });
      if (!investigationsResult?.error && investigationsResult?.data) {
        setActiveInvestigations(investigationsResult?.data);
      }

      // Load recent activities from all investigations
      const activitiesPromises = investigationsResult?.data?.slice(0, 5)?.map(inv => 
        investigationService?.getInvestigationActivities(inv?.id, 10)
      ) || [];
      
      const activitiesResults = await Promise.allSettled(activitiesPromises);
      const allActivities = activitiesResults?.filter(result => result?.status === 'fulfilled' && !result?.value?.error)?.flatMap(result => result?.value?.data || [])?.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))?.slice(0, 20);
      
      setRecentActivities(allActivities);

      // Load investigation templates
      const templatesResult = await templateService?.getTemplates();
      if (!templatesResult?.error && templatesResult?.data) {
        setTemplates(templatesResult?.data);
      }

    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadDashboardData();
  }, []);

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'LayoutDashboard' },
    { id: 'templates', label: 'Templates', icon: 'BookOpen' },
    { id: 'activity', label: 'Activity', icon: 'Activity' }
  ];

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-16">
          <div className="max-w-7xl mx-auto px-6 py-8">
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                <p className="text-text-secondary">Loading dashboard data...</p>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      {/* Main Content */}
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-6 py-8">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
              <div>
                <h1 className="text-3xl font-semibold text-text-primary">Investigation Dashboard</h1>
                <p className="text-text-secondary mt-2">
                  Manage cryptocurrency forensic investigations and trace stolen digital assets
                </p>
              </div>
              
              {/* Quick Actions */}
              <div className="flex items-center space-x-3">
                <Button
                  variant="outline"
                  iconName="Download"
                  iconPosition="left"
                  onClick={() => navigate('/forensic-report-generator')}
                >
                  Export Reports
                </Button>
                <Button
                  variant="default"
                  iconName="Plus"
                  iconPosition="left"
                  onClick={() => document.getElementById('address-input')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  New Investigation
                </Button>
              </div>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="mb-8">
            <div className="border-b border-border">
              <nav className="flex space-x-8">
                {tabs?.map((tab) => (
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    className={`
                      flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors
                      ${activeTab === tab?.id
                        ? 'border-primary text-primary' :'border-transparent text-text-secondary hover:text-text-primary hover:border-border'
                      }
                    `}
                  >
                    <Icon 
                      name={tab?.icon} 
                      size={16} 
                      color="currentColor" 
                      strokeWidth={2}
                    />
                    <span>{tab?.label}</span>
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Tab Content */}
          {activeTab === 'overview' && (
            <div className="space-y-8">
              {/* Metrics Cards - Pass real data */}
              <MetricsCards metrics={metrics} />

              {/* Address Input Form */}
              <div id="address-input">
                <AddressInputForm onStartInvestigation={handleStartInvestigation} />
              </div>

              {/* Main Content Grid */}
              <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
                {/* Active Investigations - Takes 2 columns */}
                <div className="xl:col-span-2">
                  <ActiveInvestigationsTable investigations={activeInvestigations} />
                </div>

                {/* Recent Activity - Takes 1 column */}
                <div className="xl:col-span-1">
                  <RecentActivityFeed activities={recentActivities} />
                </div>
              </div>
            </div>
          )}

          {activeTab === 'templates' && (
            <div className="space-y-8">
              {/* Templates Section */}
              <InvestigationTemplates 
                templates={templates}
                onSelectTemplate={handleSelectTemplate} 
              />
            </div>
          )}

          {activeTab === 'activity' && (
            <div className="space-y-8">
              {/* Full Width Activity Feed */}
              <RecentActivityFeed activities={recentActivities} />
              
              {/* Activity Analytics */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="bg-surface rounded-lg border border-border shadow-forensic-card p-6">
                  <div className="flex items-center space-x-3 mb-6">
                    <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg">
                      <Icon name="BarChart3" size={20} color="var(--color-primary)" strokeWidth={2} />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-text-primary">Investigation Trends</h3>
                      <p className="text-sm text-text-secondary">Real-time investigation statistics</p>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-text-secondary">Active Cases</span>
                      <span className="text-sm font-medium text-text-primary">{metrics?.activeInvestigations}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-text-secondary">Completion Rate</span>
                      <span className="text-sm font-medium text-text-primary">
                        {metrics?.totalInvestigations > 0 ? 
                          Math.round((metrics?.completedInvestigations / metrics?.totalInvestigations) * 100) : 0}%
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-text-secondary">Funds Traced</span>
                      <span className="text-sm font-medium text-text-primary">${metrics?.totalFundsTraced}M</span>
                    </div>
                  </div>
                </div>

                <div className="bg-surface rounded-lg border border-border shadow-forensic-card p-6">
                  <div className="flex items-center space-x-3 mb-6">
                    <div className="flex items-center justify-center w-10 h-10 bg-success/10 rounded-lg">
                      <Icon name="Users" size={20} color="var(--color-success)" strokeWidth={2} />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-text-primary">System Performance</h3>
                      <p className="text-sm text-text-secondary">Platform activity summary</p>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-text-secondary">Total Investigations</span>
                      <span className="text-sm font-medium text-text-primary">{metrics?.totalInvestigations}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-text-secondary">Flagged Addresses</span>
                      <span className="text-sm font-medium text-text-primary">{metrics?.flaggedAddresses}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-text-secondary">Success Rate</span>
                      <span className="text-sm font-medium text-text-primary">
                        {metrics?.totalInvestigations > 0 ? 
                          Math.round((metrics?.completedInvestigations / metrics?.totalInvestigations) * 100) : 0}%
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Quick Stats Footer */}
          <div className="mt-12 pt-8 border-t border-border">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 text-center">
              <div>
                <div className="text-2xl font-semibold text-text-primary">${metrics?.totalFundsTraced}M</div>
                <div className="text-sm text-text-secondary">Total Funds Traced</div>
              </div>
              <div>
                <div className="text-2xl font-semibold text-text-primary">{metrics?.completedInvestigations}</div>
                <div className="text-sm text-text-secondary">Cases Completed</div>
              </div>
              <div>
                <div className="text-2xl font-semibold text-text-primary">
                  {metrics?.totalInvestigations > 0 ? 
                    Math.round((metrics?.completedInvestigations / metrics?.totalInvestigations) * 100) : 0}%
                </div>
                <div className="text-sm text-text-secondary">Success Rate</div>
              </div>
              <div>
                <div className="text-2xl font-semibold text-text-primary">24/7</div>
                <div className="text-sm text-text-secondary">Real-time Monitoring</div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default InvestigationDashboard;